import { useLingui } from "@lingui/react/macro";
import { IncompleteTimePreview, ParkingOption } from "@offstreetparking/design-system/dataDisplay/ParkingOption";
import { IconButton } from "@offstreetparking/design-system/forms";
import { EditIcon } from "@offstreetparking/design-system/icons/EditIcon";
import { useFormContext } from "react-hook-form";
import type { WizardParkingChoice } from "../../../../PermitContextProvider/types";
import { mapPermitIntervals } from "../../ParkingOptionsList";
import { hasNeverExpiresIntervals } from "../../hasNeverExpiresIntervals";
import type { PermitTimeFormData } from "../../permitTimeSchema";
import { type DateSelectMode, ParkingSelectionPrompt } from "../ParkingSelectionPrompt";
const getDateSelectMode = (time: WizardParkingChoice["time"]): DateSelectMode | null => {
	const hasTimeChoice =
		(time?.startDate?.enabled || time?.startTime?.enabled || time?.endDate?.enabled || time?.endTime?.enabled) &&
		!time?.intervals?.length;
	if (!hasTimeChoice || time?.startTime?.enabled || time?.endTime?.enabled) {
		return null;
	}
	const startDateEnabled = !!time?.startDate?.enabled;
	const endDateEnabled = !!time?.endDate?.enabled;
	if (startDateEnabled && endDateEnabled) {
		return "user-select-start-and-end-dates";
	}
	if (startDateEnabled) {
		return "user-select-start-date-only";
	}
	if (endDateEnabled) {
		return "user-select-expiry-date-only";
	}
	return null;
};
type ParkingOptionContentProps = {
	parkingOption: WizardParkingChoice;
	timezone: string;
	preSelectedLocationId?: number;
	onOpenTimeModal: () => void;
	onOpenLocationModal: () => void;
	isSelectionPromptHidden?: boolean;
	isValidatingCapacity?: boolean;
};
export const ParkingOptionDetails = ({
	parkingOption,
	timezone,
	preSelectedLocationId,
	onOpenTimeModal,
	onOpenLocationModal,
	isSelectionPromptHidden = false,
	isValidatingCapacity = false,
}: ParkingOptionContentProps) => {
	const { t } = useLingui();
	const { watch, formState } = useFormContext<PermitTimeFormData>();
	const formChoices = watch("choices");
	const selectedOptionId = watch("selectedChoicesIds")?.[0];
	const hasLocationChoice = parkingOption.locationChoiceEnabled;
	const hasTimeChoice =
		(parkingOption.time?.startDate?.enabled ||
			parkingOption.time?.startTime?.enabled ||
			parkingOption.time?.endDate?.enabled ||
			parkingOption.time?.endTime?.enabled) &&
		!parkingOption.time.intervals?.length;
	const dateSelectMode = getDateSelectMode(parkingOption.time);
	const formChoice = formChoices.find((c) => c.parkingChoiceId === parkingOption.permitTypeId);
	const locations = hasLocationChoice
		? parkingOption.locations.filter((l) => formChoice?.locationIds?.includes(l.id))
		: parkingOption.locations;
	const neverExpires = hasNeverExpiresIntervals(parkingOption.time);
	const hasTimesSelected =
		(formChoice?.permitIntervals?.length ?? 0) > 0 &&
		formChoice?.permitIntervals?.every(
			(x) => x.startDate && x.startTime && (neverExpires || (x.endDate && x.endTime)),
		);
	const isSelected = selectedOptionId === parkingOption.permitTypeId;
	const isInstant = !!parkingOption.time?.startDate?.instant && !!parkingOption.time?.startTime?.instant;
	const isOutsideRecurringWindow = parkingOption.availability === "outside-recurring-window";
	const locationError =
		formState.errors.choices?.[formChoices.findIndex((c) => c.parkingChoiceId === parkingOption.permitTypeId)]
			?.locationIds?.message;
	const timeError =
		formState.errors.choices?.[formChoices.findIndex((c) => c.parkingChoiceId === parkingOption.permitTypeId)]
			?.permitIntervals?.message;
	return (
		<div className="space-y-2">
			{!isOutsideRecurringWindow &&
				((formChoice?.permitIntervals ?? []).length > 0 &&
				formChoice?.permitIntervals?.every(
					(x) => x.startDate && x.startTime && (neverExpires || (x.endDate && x.endTime)),
				) ? (
					<div className="flex items-center gap-3">
						<ParkingOption
							timezone={timezone}
							permitIntervals={mapPermitIntervals(formChoice?.permitIntervals ?? [], timezone)}
							isInstant={isInstant}
						/>
						{hasTimeChoice && (
							<IconButton
								type="button"
								onClick={onOpenTimeModal}
								intent="action"
								icon={EditIcon}
								size="xs"
								aria-label={t`Change dates and times`}
							/>
						)}
					</div>
				) : (
					<IncompleteTimePreview
						permitIntervals={formChoice?.permitIntervals ?? []}
						isInstant={isInstant}
						i18n={{
							startsNow: t`Starts Now`,
							chooseDate: t`Choose Date`,
							chooseTime: t`Choose Time`,
							chooseStartDate: t`Choose Start Date`,
							chooseStartTime: t`Choose Start Time`,
							chooseExpiryDate: t`Choose Expiry Date`,
							chooseExpiryTime: t`Choose Expiry Time`,
						}}
					/>
				))}

			<ParkingSelectionPrompt
				isSelected={isSelected}
				isSelectionPromptHidden={isSelectionPromptHidden}
				hasTimesSelected={!!hasTimesSelected}
				hasLocationChoice={hasLocationChoice}
				locations={locations}
				timeError={timeError}
				locationError={locationError}
				permitTypeId={parkingOption.permitTypeId}
				onOpenLocationModal={onOpenLocationModal}
				onOpenTimeModal={onOpenTimeModal}
				dateSelectMode={dateSelectMode}
				isLocationLocked={
					!!preSelectedLocationId && parkingOption.locations.some((l) => l.id === preSelectedLocationId)
				}
				isValidatingCapacity={isValidatingCapacity}
			/>
		</div>
	);
};
